import type { AzureComponents } from "@p0security/integrations-azure-shared/components";
import {
  VIRTUAL_MACHINE_ADMINISTRATOR_LOGIN_ROLE,
  VIRTUAL_MACHINE_USER_LOGIN_ROLE,
} from "@p0security/integrations-azure-shared/constants";
import { azureBastionHostComponentSetupFor } from "@p0security/integrations-azure-shared/helper";
import type { AzureIntegration } from "@p0security/integrations-azure-shared/types";
import type {
  FrontendInstallContext,
  FrontendInstaller,
} from "@p0security/integrations-installer";
import { TerraformLogo } from "@p0security/integrations-installer/Install/Instructions";
import { SINGLETON_ITEM } from "@p0security/shared/install/constants";
import { installedItems } from "@p0security/shared/install/installed";
import { otherInstalled } from "@p0security/shared/install/options";
import { Flex, Tabs, Typography } from "@p0security/ui";
import { widetype } from "@p0security/util/collections";
import { compact } from "lodash";
import type { ReactNode } from "react";

import { CommandDisplay } from "../CommandDisplay.tsx";

/** Builds the shell + Terraform commands that create the custom Bastion service
 * management role and assign it to the P0 Azure App Registration. */
const roleSetupCommandsFor = (
  context: FrontendInstallContext<AzureIntegration>,
  id: string
): {
  shell: { header: string; command: string }[];
  iac: { header: string; command: string }[];
} => {
  const azureApp = installedItems("azure-app", context.config);
  const { clientId, clientServicePrincipalId } = azureApp[SINGLETON_ITEM] ?? {};
  if (!clientId || !clientServicePrincipalId) {
    throw new Error("Azure App Registration is not installed.");
  }
  const { customRole } = azureBastionHostComponentSetupFor(id);
  const {
    name: roleName,
    description,
    isCustom,
    actions,
    assignableScope,
  } = customRole;
  const transformedActions = JSON.stringify(actions, null, 2);
  return {
    shell: [
      {
        header: "Target the subscription the role is being set up on",
        command: `az account set --subscription ${id}`,
      },
      {
        header:
          "Create a P0 Bastion service management role. The command creates a JSON file for the role definition, then creates the custom role from it.",
        command: `
    ${"" /** TODO: Check if the role already exists */}
  cat > p0-bastion-service-management-role.json << 'EOF'
{
  "Name": "${roleName}",
  "IsCustom": ${isCustom},
  "Description": "${description}",
  "Actions": ${transformedActions},
  "NotActions": [],
  "AssignableScopes": [
        "${assignableScope}"
  ]
}
EOF

az role definition create --role-definition  p0-bastion-service-management-role.json`,
      },
      {
        header:
          "Create role assignment of service principal to new P0 Management Role in the current subscription",
        command: `az role assignment create --assignee ${clientId} --role "${roleName}" --scope ${assignableScope}`,
      },
    ],
    iac: [
      {
        header: "Create a P0 Management Role",
        command: `resource "azurerm_role_definition" "p0_azure_custom_role_for_bastion_host" {
  name       = "${roleName}"
  scope       = "${assignableScope}"
  description = "${description}"

  permissions {
    actions     = ${transformedActions}
    not_actions = []
  }

  assignable_scopes = [
    "${assignableScope}"
  ]
}
    `,
      },
      {
        header:
          "Create role assignment of service principal to new P0 bastion host service management role in the current subscription",
        command: `
resource "azurerm_role_assignment" "p0_role_assignment" {
  depends_on         = [azurerm_role_definition.p0_azure_custom_role_for_bastion_host]
  scope              = "${assignableScope}"
  role_definition_id = azurerm_role_definition.p0_azure_custom_role_for_bastion_host.role_definition_resource_id
  principal_type     = "ServicePrincipal"

  principal_id = "${clientServicePrincipalId}"
}`,
      },
    ],
  };
};

/** Renders the custom-role setup help and commands shown for the Bastion
 * options (single / subscription) directly above the Role Definition ID field.
 * Jump hosts don't require a custom role, so this is scoped to those options. */
const BastionRoleSetup = (props: {
  context: FrontendInstallContext<AzureIntegration>;
  id: string;
}) => {
  const { context, id } = props;
  const { shell, iac } = roleSetupCommandsFor(context, id);
  const renderCommands = (commands: { header: string; command: string }[]) =>
    commands.map(({ header, command }, ix) => (
      <div key={ix}>
        <Typography.Paragraph>{header}</Typography.Paragraph>
        <CommandDisplay commands={command} maxRows={20} />
      </div>
    ));
  return (
    <Flex gap="0.7em" vertical>
      <Typography.Paragraph>
        In order to provision SSH access to your Azure subscription, we need to
        create a custom role and assign it to the Azure App Registration
        you&apos;ve already integrated with P0.
      </Typography.Paragraph>
      <Tabs
        items={compact([
          { key: "shell", label: "Shell", children: renderCommands(shell) },
          {
            key: "iac",
            label: <>{TerraformLogo}&nbsp;Terraform</>,
            children: renderCommands(iac),
          },
        ])}
      />
    </Flex>
  );
};

/** Builds the CLI command that looks up a built-in role's definition ID. */
const roleLookupCommand = (roleName: string) =>
  `az role definition list --name "${roleName}" --query "[0].id" -o tsv`;

const jumpHostStandardRoleInstaller = {
  labeler: (): ReactNode => (
    <CommandDisplay
      commands={roleLookupCommand(VIRTUAL_MACHINE_USER_LOGIN_ROLE.name)}
    />
  ),
};

const adminRoleInstaller = {
  labeler: (): ReactNode => (
    <CommandDisplay
      commands={roleLookupCommand(
        VIRTUAL_MACHINE_ADMINISTRATOR_LOGIN_ROLE.name
      )}
    />
  ),
};

/** Renders the P0 Bastion service management role setup (required only for
 * the managed Bastion option, so P0 itself can open tunnels), followed by the
 * lookup command for the actual standard-access role granted to connecting
 * users. Without the latter, this field ends up holding the management
 * role's ID instead of a user-access role like Virtual Machine User Login. */
const azureBastionStandardRoleInstaller = {
  labeler: (
    context: FrontendInstallContext<AzureIntegration>,
    id: string
  ): ReactNode => (
    <Flex gap="0.7em" vertical>
      <BastionRoleSetup context={context} id={id} />
      <Typography.Paragraph>
        Then, grant your users a role for standard (non-sudo) access through the
        bastion. The built-in {VIRTUAL_MACHINE_USER_LOGIN_ROLE.name} role is
        recommended; look up its role definition ID with:
      </Typography.Paragraph>
      <CommandDisplay
        commands={roleLookupCommand(VIRTUAL_MACHINE_USER_LOGIN_ROLE.name)}
      />
    </Flex>
  ),
};

export const azureBastionHostInstaller: FrontendInstaller<
  typeof AzureComponents
>["components"]["bastion-host"] = {
  // No component-level instructions: after the subscription is selected the
  // user picks how P0 connects (single / subscription bastion host, or a custom
  // jump host) and the per-option fields render inline.
  optionProvider: async (context) => {
    const iamWriteItems =
      await otherInstalled<AzureIntegration>("iam-write")(context);
    const alreadyInstalled = widetype.keys(
      context.config["bastion-host"] ?? {}
    );
    const filteredItems = iamWriteItems.filter(
      (item) => !alreadyInstalled.includes(item.id)
    );
    return filteredItems;
  },

  items: {
    bastion: {
      options: {
        azureBastion: {
          items: {
            // The custom-role setup script and the standard-access role lookup
            // command render above the Standard user role id field; selecting a
            // bastion host (single / subscription) comes next.
            standardAccessRoleId: azureBastionStandardRoleInstaller,
            adminAccessRoleId: adminRoleInstaller,
            bastionHost: {
              options: {
                single: {
                  items: {},
                },
                subscription: {
                  items: {
                    subscriptionId: {
                      optionProvider: async (context, _item, id) => {
                        const items = widetype
                          .entries(
                            installedItems("bastion-host", context.config)
                          )
                          .filter(
                            ([key, value]) =>
                              key !== id &&
                              value?.bastion.type === "azureBastion" &&
                              value.bastion.bastionHost.type === "single"
                          )
                          .map(([id, value]) => ({
                            id,
                            label: value?.label ?? id,
                          }));

                        return items;
                      },
                    },
                  },
                },
              },
            },
          },
        },
        jumpHost: {
          items: {
            standardAccessRoleId: jumpHostStandardRoleInstaller,
            adminAccessRoleId: adminRoleInstaller,
          },
        },
      },
    },
  },
};
